<template>
  <div class="cms-faq-management">
    <cms-page-header
      :title="$t('cms.faq.manageFaqs')"
      :create-text="$t('cms.faq.createFaq')"
      @create="navigateToCreate"
      @back="goBack"
    />

    <div v-if="categoryName" class="cms-faq-category-info">
      <h3>{{ categoryName }}</h3>
    </div>

    <div v-if="isLoading" class="cms-loading">
      <div class="cms-loading-spinner" />
      <p>{{ $t('general.loading') }}</p>
    </div>

    <cms-empty-state
      v-else-if="faqs.length === 0"
      :message="$t('cms.faq.noFaqs')"
      :hint="$t('cms.faq.createFirstFaq')"
    />

    <div v-else class="cms-faq-list">
      <div v-for="(faq, index) in faqs" :key="faq.id || `temp-${index}`" class="cms-faq-item">
        <div class="cms-faq-item-content">
          <h4 class="cms-faq-question">{{ faq.question }}</h4>
          <div class="cms-faq-answer" v-html="faq.answer"></div>
        </div>
        <div class="cms-faq-item-actions">
          <cms-button variant="primary" size="sm" icon="pencil" @click="navigateToEdit(faq.id)">
            {{ $t('general.edit') }}
          </cms-button>
          <cms-button variant="danger" size="sm" icon="trash" :disabled="deletingId === faq.id" @click="deleteFaq(faq)">
            {{ $t('general.delete') }}
          </cms-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import {
  CmsPageHeader,
  CmsButton,
  CmsEmptyState,
} from '@/components/cms';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';

interface FaqItem {
  id?: number;
  question: string;
  answer: string;
  order: number;
  is_public: boolean;
}

export default defineComponent({
  name: 'TheCmsFaqManagement',
  components: {
    CmsPageHeader,
    CmsButton,
    CmsEmptyState,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const toast = useToast();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const categoryId = computed(() => Number(route.params.categoryId));
    const faqs = ref<FaqItem[]>([]);
    const categoryName = ref('');
    const isLoading = ref(false);
    const deletingId = ref<number | null>(null);
    const currentLanguage = ref('en');

    const extractListData = (response: any): any[] => {
      if (!response) return [];
      const candidates = [
        response?.data?.data,
        response?.data,
        response?.data?.data?.data,
        response,
      ];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const loadCategory = async () => {
      if (!agencySlug.value || !categoryId.value) return;

      try {
        const response = await api.faqCategoryService.getCmsFaqCategory(agencySlug.value, categoryId.value);
        const category = normalizeResource(response) || response;
        const translations = normalizeResource(extractRelationship(response, 'translations')) || [];
        const translation = translations.find((t: any) => t.locale === currentLanguage.value) || translations[0] || {};
        categoryName.value = translation.name || '';
      } catch (error) {
        console.error('Failed to load category', error);
      }
    };

    const loadFaqs = async () => {
      if (!agencySlug.value || !categoryId.value) return;

      isLoading.value = true;
      try {
        const response = await api.faqService.listCmsFaqs(agencySlug.value, {
          filters: {
            cms_faq_category_id: categoryId.value,
          },
          perPage: 1000,
        });

        const faqsData = extractListData(response);
        const locale = currentLanguage.value;

        faqs.value = faqsData
          .map((faq: any) => {
            const normalized = normalizeResource(faq) || faq;
            const translations = normalizeResource(extractRelationship(faq, 'translations')) || [];
            const translation = translations.find((t: any) => t.locale === locale) || translations[0] || {};

            return {
              id: normalized.id,
              question: translation.question || '',
              answer: translation.answer || '',
              order: normalized.order || 0,
              is_public: normalized.is_public || false,
            };
          })
          .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
      } catch (error) {
        console.error('Error loading FAQs', error);
        toast.error(t('general.errorMessage'));
      } finally {
        isLoading.value = false;
      }
    };

    const navigateToCreate = () => {
      router.push({ name: 'cms.help-center.create-faq', query: { category_id: categoryId.value } });
    };

    const navigateToEdit = (faqId: number) => {
      router.push({ name: 'cms.help-center.edit-faq', params: { id: faqId } });
    };

    const deleteFaq = async (faq: FaqItem) => {
      if (!agencySlug.value || !faq.id) return;
      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) {
        return;
      }

      deletingId.value = faq.id;
      try {
        await api.faqService.deleteCmsFaq(agencySlug.value, faq.id);
        faqs.value = faqs.value.filter((item) => item.id !== faq.id);
        toast.success(t('cms.faq.deleteSuccess'));
      } catch (error: any) {
        console.error('Failed to delete FAQ', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        deletingId.value = null;
      }
    };

    const goBack = () => {
      router.push({ name: 'cms.help-center' });
    };

    onMounted(async () => {
      await loadCategory();
      await loadFaqs();
    });

    return {
      faqs,
      categoryName,
      isLoading,
      deletingId,
      navigateToCreate,
      navigateToEdit,
      deleteFaq,
      goBack,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-faq-management {
  color: $cms-text-primary;
}

.cms-faq-category-info {
  margin-bottom: $cms-spacing-lg;
  padding: $cms-spacing-md;
  background: $cms-background-dark-light;
  border-radius: $cms-radius-md;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }
}

.cms-faq-list {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-md;
}

.cms-faq-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $cms-spacing-md;
  padding: $cms-spacing-lg;
  background: $cms-background-dark-light;
  border-radius: $cms-radius-md;

  .cms-faq-item-content {
    flex: 1;

    .cms-faq-question {
      margin: 0 0 $cms-spacing-sm 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: $cms-text-primary;
    }

    .cms-faq-answer {
      color: $cms-text-secondary;
      font-size: 0.875rem;
      line-height: 1.5;
    }
  }

  .cms-faq-item-actions {
    display: flex;
    gap: $cms-spacing-sm;
  }
}

.cms-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $cms-spacing-xxl 0;
  gap: $cms-spacing-sm;
  color: $cms-text-secondary;
}

.cms-loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid $cms-border-color-light;
  border-top-color: $cms-primary-color;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

